import React, { useRef, useState } from 'react';
import LeftChevronIcon from '../Icons/LeftChevronIcon';
import RightChevronIcon from '../Icons/RightChevronIcon';

const TabSlider = ({
  slides = [],
  type = 'landscape', // 'landscape' or 'portrait'
  className = '',
  config,
  ...props
}) => {
  const scrollContainerRef = useRef(null);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);

  const scrollToNext = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = 256; // 240px slide width + 16px gap
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      
      // Check scroll state after scrolling
      setTimeout(() => {
        const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
        const isAtStart = container.scrollLeft <= 1;
        setCanScrollRight(!isAtEnd);
        setCanScrollLeft(!isAtStart);
      }, 300);
    }
  };

  const scrollToPrevious = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = 256; // 240px slide width + 16px gap
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      
      // Check scroll state after scrolling
      setTimeout(() => {
        const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
        const isAtStart = container.scrollLeft <= 1;
        setCanScrollRight(!isAtEnd);
        setCanScrollLeft(!isAtStart);
      }, 300);
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
      const isAtStart = container.scrollLeft <= 1;
      setCanScrollRight(!isAtEnd);
      setCanScrollLeft(!isAtStart);
    }
  };

  const handleSlideClick = (link) => {
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div 
      className={`relative w-full pb-v3-xs ${className}`}
      {...props}
    >
      {/* Scrollable Container */}
      <div
        ref={scrollContainerRef}
        className="flex gap-v3-sm overflow-x-auto no-scrollbar pb-v3-xs"
        onScroll={handleScroll}
      >
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`flex-shrink-0 transition-transform duration-200 bg-white rounded-[14px] flex flex-col items-start ${
              type === 'portrait'
                ? 'gap-[10px] flex-1 self-stretch min-w-[260px]'
                : 'w-[240px] gap-v3-xs'
            } ${index === slides.length - 1 ? 'mr-v3-sm ' : ''} ${
              slide.link ? 'group cursor-pointer' : 'cursor-default'
            }`}
            style={{
              '--brand-color': config?.brand_color || 'var(--v3-purple-primary)',
            }}
            onClick={() => slide.link && handleSlideClick(slide.link)}
          >
            {/* Image Container */}
            <div className={`flex flex-col justify-center items-center pt-v3-xxs px-v3-xxs ${
              type === 'portrait' ? 'pb-0 gap-[10px]' : 'pb-0 gap-v3-xs'
            }`}>
              <img
                src={slide.image}
                alt={slide.title || 'Slide image'}
                className={`rounded-v3-md object-cover ${
                  type === 'portrait'
                    ? 'w-[236px] h-[296px]'
                    : 'h-[160px] w-[236px] self-stretch'
                }`}
                style={{ backgroundColor: 'lightgray' }}
              />
            </div>
            
            {/* Text Content */}
            {(slide.title || slide.description) && (
              <div
                className={`flex flex-col items-start gap-v3-xs ${
                  type === 'portrait' ? 'p-v3-sm' : 'p-v3-sm'
                }`}
              >
                {slide.title && (
                  <h3
                    className={`font-inter-variable text-sm font-bold leading-[160%] transition-colors duration-200 ${
                      slide.link ? 'group-hover:!text-[var(--brand-color)]' : ''
                    }`}
                    style={{
                      color: config?.element_color || 'var(--v3-black-max)',
                      fontFamily: config?.font_name ? `${config.font_name}, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif` : undefined,
                      fontSize: config?.font_size ? `${config.font_size}px` : undefined,
                    }}
                  >
                    {slide.title}
                  </h3>
                )}
                {slide.description && (
                  <p
                    className={`font-inter-variable text-sm font-regular leading-5 transition-colors duration-200 ${
                      slide.link ? 'group-hover:!text-[var(--brand-color)]' : ''
                    }`}
                    style={{
                      color: config?.element_color ? `${config.element_color}B3` : 'var(--v3-black-secondary)', // 70% opacity for description
                      fontFamily: config?.font_name ? `${config.font_name}, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif` : undefined,
                      fontSize: config?.font_size ? `${Math.round(config.font_size * 0.9)}px` : undefined, // Slightly smaller for description
                    }}
                  >
                    {slide.description}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Buttons */}
      {canScrollLeft && (
        <button
          onClick={scrollToPrevious}
          className="absolute left-0 top-1/2 -translate-y-1/2 flex w-10 h-10 justify-center items-center gap-[10px] aspect-square rounded-v3-xl bg-white shadow-v3-double hover:bg-v3-purple-bg transition-colors duration-200"
          aria-label="Scroll to previous slide"
        >
          <LeftChevronIcon className='text-v3-purple-primary' />
        </button>
      )}
      
      {canScrollRight && (
        <button
          onClick={scrollToNext}
          className="absolute right-0 top-1/2 -translate-y-1/2 flex w-10 h-10 justify-center items-center gap-[10px] aspect-square rounded-v3-xl bg-white shadow-v3-double hover:bg-v3-purple-bg transition-colors duration-200"
          aria-label="Scroll to next slide"
        >
          <RightChevronIcon />
        </button>
      )}
    </div>
  );
};

export default TabSlider;
